import { IAddressDto, ISsrDataDto, ISsrDataLineDto, ISsrLineItemDto, ManualOrderReasonEnumId, SsrStateEnumId } from '@classictechsolutions/hubapi-transpiled-enums';
import { isNullOrWhiteSpace } from 'cb-hub-lib';
import { findIndex, remove, sumBy } from 'lodash';
import { Observable, tap, take } from 'rxjs';
import { BaseMappedItem } from '../base/base-mapped-item';
import { Computed } from '../base/computed-prop.decorator';
import { DtoProp } from '../base/dto-prop.decorator';
import { IPurchaseOrderDto } from '@app/logic/purchase-orders';
import { ISsrMappedItem } from './interfaces/i.ssr.mapped';
import { ISsrsLogicService } from './interfaces/i.ssrs.logic.service';

export class SsrMappedItem
    extends BaseMappedItem<ISsrDataDto, ISsrMappedItem, ISsrsLogicService>
    implements ISsrMappedItem {
    @DtoProp public readonly id: number;
    @DtoProp public activityEnd: string;
    @DtoProp public activityName: string;
    @DtoProp public businessAccountAddress: IAddressDto;
    @DtoProp public businessAccountLegalName: string;
    @DtoProp public businessAccountContactOrderRolesEmails: string[];
    @DtoProp public businessEntityAccountsPhone: string;
    @DtoProp public businessEntityAddress: IAddressDto;
    @DtoProp public businessEntityCreditorsEmail: string;
    @DtoProp public businessEntityEmail: string;
    @DtoProp public businessEntityLegalName: string;
    @DtoProp public businessEntityWebsite: string;
    @DtoProp public comments: string;
    @DtoProp public complianceItem: string;
    @DtoProp public complianceStatement: string;
    @DtoProp public consentNumber: string;
    @DtoProp public constructionManagerEmail: string;
    @DtoProp public constructionManagerMobile: string;
    @DtoProp public constructionManagerName: string;
    @DtoProp public dpNumber: string;
    @DtoProp public duration: number;
    @DtoProp public ssrNumber: number;
    @DtoProp public endDate: string;
    @DtoProp public gst: number;
    @DtoProp public jobNumber: string;
    @DtoProp public lineItems: ISsrDataLineDto[];
    @DtoProp public lotAddress: IAddressDto;
    @DtoProp public state: SsrStateEnumId;
    @DtoProp public mainContactEmail: string;
    @DtoProp public mainContactMobile: string;
    @DtoProp public submittedForApprovalById: string;
    @DtoProp public mainContactName: string;
    @DtoProp public orderDate: string;
    @DtoProp public orderNumber: string;
    @DtoProp public splitActivityKey: any;
    @DtoProp public splitActivityPercentage: number;
    @DtoProp public stageName: string;
    @DtoProp public startDate: string;
    @DtoProp public total: number;
    @DtoProp public totalWithGst: number;
    @DtoProp public valueRetainedPercentage: number;
    @DtoProp public isDraft: boolean;
    @DtoProp public businessEntityInvoiceEmail: string;
    @DtoProp public lotNumber: number;
    @DtoProp public activityCode: string;
    @DtoProp public lotIsAShowHome: boolean;
    @DtoProp public activityGroupName: string;
    @DtoProp public lotProject: string;
    @DtoProp public lotType: string;

    @Computed()
    public get hasComplianceStatement(): boolean {
        return this?.complianceStatement?.length > 0;
    }

    constructor(
        sourceData: ISsrDataDto,
        logicService: ISsrsLogicService
    ) {
        super(sourceData, logicService, SsrMappedItem);
    }



    public lineItemTotal(): number {
        return sumBy(this.lineItems, (item: ISsrDataLineDto) => (item.rate || 0) * item.quantity);
    }

    public removeLineItem(lineItemId: number): Observable<ISsrLineItemDto> {
        return this.$logicService
            .removeLineItem(this.id, lineItemId)
            .pipe(
                tap(result => {
                    remove(this.lineItems, { id: result.id });
                })
            );
    }

    public saveComments(): Observable<string> {
        return this.$logicService
            .updateComments(this.id, this.comments)
            .pipe(
                tap(result => this.$updateThisAndOriginal(result))
            );
    }

    public convertToManualOrder(orderReasonId?: ManualOrderReasonEnumId, orderReason?: string): Observable<IPurchaseOrderDto> {
        return this.$logicService
            .convertToManualOrder(this.id, { orderReasonId, orderReason });
    }

    public updateLineItemQuantity(lineItemId: number, quantity: number): Observable<ISsrDataLineDto> {
        return this.$logicService
            .updateLineItemQuantity(lineItemId, quantity)
            .pipe(
                tap(this.handleUpdateLineItemQuantity)
            );
    }

    public getPossibleLineItems(): Observable<ISsrLineItemDto[]> {
        return this.$logicService
            .getPossibleLineItems(this.id);
    }

    public addLineItems(items: ISsrLineItemDto[]): Observable<ISsrLineItemDto[]> {
        return this.$logicService
            .addLineItems(this.id, items)
            .pipe(
                tap(() => {
                    // BUILD PROGRAMME TODO - the addLineItems endpoint should return the updated SsrDto
                    // which would remove the need for a separate reload
                    this.$reload().pipe(take(1)).subscribe();
                })
            );
    }

    private readonly handleUpdateLineItemQuantity = (result: ISsrDataLineDto): void => {
        const index = findIndex(this.lineItems, { id: result.id });
        if (index < 0) {
            return;
        }
        this.lineItems[index].quantity = result.quantity;
        this.lineItems[index].subtotal = this.lineItems[index].quantity * this.lineItems[index].rate;
        if (isNullOrWhiteSpace(this.lineItems[index].code)) {
            this.lineItems[index].unallocatedQuantity = result.unallocatedQuantity;
        } else {
            this.lineItems.forEach(item => {
                if (item.code === this.lineItems[index].code) {
                    item.unallocatedQuantity = result.unallocatedQuantity;
                }
            });
        }
    };
}
